import { ArrowRight, Check, ChevronRight, Link2, LockKeyhole, Star } from 'lucide-react';
import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { categoryLabels, tools } from '../registry';
import { toolPresentation } from '../presentation';
import { selectNextStepTools } from '../discovery';
import { toolboxCategoryUrl, toolboxUrl } from '../routing';
import type { ToolDefinition } from '../types';
import { loadToolUsage, saveToolUsage, withFavorite } from '../usage';

interface ToolPageProps {
  children: ReactNode;
  definition: ToolDefinition;
  onNavigate: (path: string) => void;
}

function followInternalLink(event: ReactMouseEvent<HTMLAnchorElement>, path: string, navigate: (path: string) => void) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(path);
}

export function ToolPage({ children, definition, onNavigate }: ToolPageProps) {
  const directConnection = definition.dataFlow === 'direct';
  const favoriteId = definition.parentToolId || definition.id;
  const parent = definition.parentToolId ? tools.find((tool) => tool.id === definition.parentToolId) : undefined;
  const [favorite, setFavorite] = useState(() => loadToolUsage().favorites.includes(favoriteId));
  const [shared, setShared] = useState<'' | 'copied' | 'shared'>('');
  const [shareError, setShareError] = useState('');
  const nextSteps = useMemo(() => selectNextStepTools(definition, tools), [definition]);

  useEffect(() => { setFavorite(loadToolUsage().favorites.includes(favoriteId)); setShared(''); setShareError(''); }, [favoriteId]);

  function toggleFavorite() {
    const next = withFavorite(loadToolUsage(), favoriteId);
    saveToolUsage(next);
    setFavorite(next.favorites.includes(favoriteId));
  }

  async function shareTool() {
    const url = window.location.href;
    setShareError('');
    setShared('');
    try {
      if (navigator.share) { await navigator.share({ title: definition.title, text: definition.summary, url }); setShared('shared'); }
      else if (navigator.clipboard) { await navigator.clipboard.writeText(url); setShared('copied'); }
      else {
        const input = document.createElement('input'); input.value = url; document.body.append(input); input.select();
        try { if (!document.execCommand('copy')) throw new Error('无法复制'); setShared('copied'); } finally { input.remove(); }
      }
      window.setTimeout(() => setShared(''), 1400);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setShareError('分享失败，可复制地址栏中的链接。');
    }
  }

  return (
    <main className="tool-page" data-usage-tool={definition.slug} data-presentation={toolPresentation(definition)}>
      <div className="tool-page-topline">
        <nav aria-label="面包屑" className="tool-breadcrumb"><a href="/tools" onClick={(event) => followInternalLink(event, '/tools', onNavigate)}>全部工具</a><ChevronRight size={13} /><a href={toolboxCategoryUrl(definition.category)} onClick={(event) => followInternalLink(event, toolboxCategoryUrl(definition.category), onNavigate)}>{categoryLabels[definition.category]}</a><ChevronRight size={13} /><span>{definition.title}</span></nav>
        <div className="tool-page-actions"><button aria-label={favorite ? '取消收藏' : '收藏工具'} className={favorite ? 'is-active' : ''} data-analytics-id="tool.favorite" onClick={toggleFavorite} type="button"><Star fill={favorite ? 'currentColor' : 'none'} size={15} /><span>{favorite ? '已收藏' : '收藏'}</span></button><button aria-label={shared === 'shared' ? '已分享' : shared === 'copied' ? '链接已复制' : '分享工具'} data-analytics-id="tool.share" onClick={() => void shareTool()} type="button">{shared ? <Check size={15} /> : <Link2 size={15} />}<span>{shared === 'shared' ? '已分享' : shared === 'copied' ? '链接已复制' : '分享'}</span></button></div>
      </div>
      {shareError && <p className="tool-error-inline" role="alert">{shareError}</p>}
      <h1 className="tool-visually-hidden">{definition.title}</h1>
      {parent && definition.showPresetNotice !== false && <aside aria-label="任务预设" className="tool-task-context"><span><Check size={13} />已应用“{definition.title}”预设</span><a href={toolboxUrl(parent.slug)} onClick={(event) => followInternalLink(event, toolboxUrl(parent.slug), onNavigate)}>完整工具：{parent.title} <ArrowRight size={14} /></a></aside>}
      <div className="tool-surface">{children}</div>
      {definition.help.length > 0 && <details className="tool-help"><summary><span>使用帮助</span><ChevronRight size={15} /></summary><div>{definition.help.map((item) => <details key={item.question}><summary>{item.question}<ChevronRight size={15} /></summary><p>{item.answer}</p></details>)}</div></details>}
      <section aria-label={directConnection ? '直连说明' : '隐私说明'} className="tool-privacy-note">
        <LockKeyhole size={18} />
        {directConnection
          ? <p><strong>浏览器直连：</strong>请求不经过微言服务器，但目标服务可读取查询与连接信息；页面不保存输入。</p>
          : <p>文件与结果仅在当前浏览器处理，不上传微言服务器。</p>}
      </section>
      <section className="tool-related">
        <header><div><h2>相关工具</h2></div><a href={toolboxCategoryUrl(definition.category)} onClick={(event) => followInternalLink(event, toolboxCategoryUrl(definition.category), onNavigate)}>查看{categoryLabels[definition.category]} <ArrowRight size={14} /></a></header>
        <div>{nextSteps.map((tool) => { const path = toolboxUrl(tool.slug); return <a href={path} key={tool.id} onClick={(event) => followInternalLink(event, path, onNavigate)}><span><strong>{tool.title}</strong></span><ArrowRight size={14} /></a>; })}</div>
      </section>
    </main>
  );
}

export function ToolPanel({ children, title, description, collapsible = false }: { children: ReactNode; title?: string; description?: string; collapsible?: boolean }) {
  if (collapsible && title) return <details className="tool-disclosure"><summary>{title}<ChevronRight size={15} /></summary><div className="tool-disclosure-content">{description && <p>{description}</p>}{children}</div></details>;
  return <section className="tool-panel">{title && <header><h2>{title}</h2>{description && <p>{description}</p>}</header>}{children}</section>;
}
